/**
 * Dashboard / Worklists (Owner/Admin, worklist:read). A health band of seven count tiles up top, split
 * into two labelled groups — "Act now" (renewal / churn / trial_expiring) and "Review" (license
 * discrepancy / underutilized / add-on waste / lapsed trials) — then the scannable tables below, each
 * headed by a tone-matched count pill so the band and the lists read as one system. A Viewer never
 * reaches here (the route asserts the permission and the nav hides it).
 *
 * r1 critique (24/7): the band used to paint all six semantic medallions solid regardless of count (churn
 * showed a full red block sitting at 0) plus a per-tile colour-matched hover border and a shadow on top of
 * the card's own shadow — too busy, too colourful, and colour stopped meaning a LIVE status the moment it
 * became a permanent label. Now: a medallion is a quiet neutral fill until its count is actually > 0
 * (kindPresentation.ts's medallionClass — shared with HomePage), no per-tile hover border, no medallion
 * shadow, and the label wraps to two lines instead of truncating ("License discrepan…").
 *
 * Still deliberately flat and calm: no hero-metric, no icon-card filler — just the counts and the lists a
 * CSM works from. Each tile jumps to its list; each row opens that customer's Customer-360.
 */

import { useTranslation } from "react-i18next";
import { ChevronRight, Layers, Sprout } from "lucide-react";
import type { ReactNode } from "react";
import { PermissionGate } from "@/components/portal/PermissionGate";
import { FreshnessBadge } from "@/components/portal/FreshnessBadge";
import { ErrorState, PendingNumber, StatusPill, type StatusTone } from "@/components/ui";
import { Section } from "@/features/customer/display";
import { cn } from "@/lib/cn";
import { formatInt } from "@/lib/format";
import type { WorklistKind } from "@/api/types";
import { WorklistTable } from "@/features/worklists/WorklistTable";
import { AddonWasteTable } from "@/features/worklists/AddonWasteTable";
import { LapsedTrialTable } from "@/features/worklists/LapsedTrialTable";
import { KIND_PRESENTATION, medallionClass } from "@/features/worklists/kindPresentation";
import {
  WORKLIST_KINDS,
  useAddonWaste,
  useAllWorklists,
  useLapsedTrial,
  useWorklistSummary,
} from "@/features/worklists/useWorklists";

export function DashboardPage() {
  const { t } = useTranslation();

  return (
    <PermissionGate
      permission="worklist:read"
      fallback={<ErrorState title={t("state.forbiddenTitle")} body={t("state.forbiddenBody")} />}
    >
      <DashboardBody />
    </PermissionGate>
  );
}

function DashboardBody() {
  const { t } = useTranslation();
  // Both the band tiles and the tables below read the worklist views, which are now a pass-through over
  // the mv_account_360 snapshot — so this page needs the SAME freshness disclosure Home carries. The
  // counts still come per-kind (useAllWorklists) so the tables stay live-scrollable; the summary is
  // fetched ONLY for its refreshed_at/stale here (its counts are unused on this page).
  const summary = useWorklistSummary();
  const queries = useAllWorklists();
  const queryByKind = Object.fromEntries(
    WORKLIST_KINDS.map((kind, i) => [kind, queries[i]]),
  ) as Record<WorklistKind, (typeof queries)[number] | undefined>;
  const addonWaste = useAddonWaste();
  const addonWasteCount =
    addonWaste.isPending || addonWaste.isError ? null : (addonWaste.data?.count ?? 0);
  const lapsedTrial = useLapsedTrial();
  const lapsedTrialCount =
    lapsedTrial.isPending || lapsedTrial.isError ? null : (lapsedTrial.data?.count ?? 0);

  function kindTile(kind: WorklistKind) {
    const q = queryByKind[kind];
    const { icon, tone, labelKey } = KIND_PRESENTATION[kind];
    return (
      <Tile
        key={kind}
        href={`#wl-${kind}`}
        icon={icon}
        tone={tone}
        label={t(labelKey)}
        count={q?.isError ? null : (q?.data?.count ?? null)}
        pending={q?.isPending ?? true}
      />
    );
  }

  return (
    <section aria-labelledby="dashboard-title" className="flex flex-col gap-8">
      <header className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
          <h1
            id="dashboard-title"
            className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
          >
            {t("nav.dashboard")}
          </h1>
          {/* mv_account_360 refresh freshness — same fact Home shows; the band tiles + tables below all
              read the matview snapshot, so a stale refresh must be disclosed HERE too, not just on Home. */}
          {summary.data && (
            <FreshnessBadge
              kind="worklist"
              refreshedAt={summary.data.refreshed_at}
              stale={summary.data.stale}
            />
          )}
        </div>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t("worklist.subtitle")}</p>
      </header>

      {/* Health band: seven tiles in two labelled groups (≤4 each) so the strip scans as two questions —
          "what needs action" vs "what's worth a look" — instead of one flat wall of sameweight tiles. */}
      <div className="flex flex-col gap-5">
        <TileGroup title={t("worklist.band.actNow")}>
          {kindTile("renewal")}
          {kindTile("churn")}
          {kindTile("trial_expiring")}
        </TileGroup>

        <TileGroup title={t("worklist.band.review")}>
          {kindTile("upsell")}
          {kindTile("underutilized")}
          {/* Add-on seat waste — a fifth, purpose-built list (warn tone, like underutilized). */}
          <Tile
            href="#wl-addon_waste"
            icon={<Layers className="size-5" aria-hidden />}
            tone="warn"
            label={t("worklist.addonWaste.title")}
            count={addonWasteCount}
            pending={addonWaste.isPending}
          />
          {/* Lapsed trials — a PROSPECT list, so a NEUTRAL tile and a sprout: an opportunity, not a
              fault. Deliberately the quietest tile in the band regardless of count — medallionClass never
              turns a "neutral" tone vivid, so this can't start shouting like the tiles above it. If it
              did, the operator would learn to tune out the shouting — and the one they must never tune
              out is the one above. */}
          <Tile
            href="#wl-lapsed_trial"
            icon={<Sprout className="size-5" aria-hidden />}
            tone="neutral"
            label={t("worklist.lapsedTrial.title")}
            count={lapsedTrialCount}
            pending={lapsedTrial.isPending}
          />
        </TileGroup>
      </div>

      {/* The four lists. Each section header carries its tone-matched count pill. */}
      {WORKLIST_KINDS.map((kind, i) => {
        const q = queries[i];
        const count = q?.isPending || q?.isError ? null : (q?.data?.count ?? 0);
        return (
          <Section
            key={kind}
            id={`wl-${kind}`}
            className="scroll-mt-20"
            title={t(`worklist.${kind}`)}
            trailing={
              count != null ? (
                <StatusPill tone={KIND_PRESENTATION[kind].tone}>{formatInt(count)}</StatusPill>
              ) : undefined
            }
          >
            <WorklistTable kind={kind} />
          </Section>
        );
      })}

      {/* Lapsed trials — a PROSPECT list. Neutral tone throughout, and it must stay that way: if this
          looked as urgent as the list above, the operator would stop trusting the red. */}
      <Section
        id="wl-lapsed_trial"
        className="scroll-mt-20"
        title={t("worklist.lapsedTrial.title")}
        trailing={
          lapsedTrialCount != null ? (
            <StatusPill tone="neutral">{formatInt(lapsedTrialCount)}</StatusPill>
          ) : undefined
        }
      >
        <LapsedTrialTable />
      </Section>

      {/* The add-on seat-waste list (its own purpose-built table + tone-matched count pill). */}
      <Section
        id="wl-addon_waste"
        className="scroll-mt-20"
        title={t("worklist.addonWaste.title")}
        trailing={
          addonWasteCount != null ? (
            <StatusPill tone="warn">{formatInt(addonWasteCount)}</StatusPill>
          ) : undefined
        }
      >
        <AddonWasteTable />
      </Section>
    </section>
  );
}

/** One labelled group of ≤4 tiles ("Act now" / "Review") — a quiet uppercase caption, not a card of its
 *  own, so the band still reads as one strip rather than two boxed-off widgets. */
function TileGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-(length:--text-xs) font-semibold uppercase tracking-wide text-(--text-subtle)">
        {title}
      </h2>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3">{children}</div>
    </div>
  );
}

interface TileProps {
  href: string;
  icon: ReactNode;
  tone: StatusTone;
  label: string;
  count: number | null;
  pending: boolean;
}

/** One health-band tile — jump-link to its table below. No per-tile hover-border tint and no medallion
 *  shadow (r1 critique, 24/7 — too busy/colourful); the medallion itself is neutral until its count is
 *  actually > 0 (medallionClass, kindPresentation.ts). The label wraps to 2 lines instead of truncating,
 *  so "License discrepancy" never clips to "License discrepan…". */
function Tile({ href, icon, tone, label, count, pending }: TileProps) {
  const active = !pending && count != null && count > 0;
  return (
    <a
      href={href}
      className={cn(
        "group flex items-center gap-4 rounded-(--radius) border border-(--border) bg-(--surface) p-4 shadow-(--shadow-sm)",
        "transition-shadow hover:shadow-(--shadow-md)",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)",
      )}
    >
      <span
        className={cn(
          "grid size-12 shrink-0 place-items-center rounded-(--radius-sm)",
          medallionClass(tone, active),
        )}
        aria-hidden
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="tabular text-(length:--text-2xl) font-semibold leading-none text-(--text-strong)">
          {pending ? (
            <PendingNumber />
          ) : count == null ? (
            <span className="text-(--text-subtle)">—</span>
          ) : (
            formatInt(count)
          )}
        </span>
        <span className="line-clamp-2 text-(length:--text-sm) font-medium leading-snug text-(--text-muted)">
          {label}
        </span>
      </span>
      <ChevronRight
        className="ml-auto size-4 shrink-0 text-(--text-subtle) opacity-0 transition-opacity group-hover:opacity-100"
        aria-hidden
      />
    </a>
  );
}
